import './tableCardViewV2.less'
// import ButtonsCardListView from '../ButtonsCardListView/buttonsCardListView'
import Table, { ITableColumn, TableParams } from '../dataTable/dataTable_V2'
import TableLegend from '../TableLegend'
import PaginationView from '../Pagination/pagination'
import useUrlContext from '../../hooks/useUrlContext'
import { IS_ALL, LIMIT_DEFAULT, PAGE, PAGE_VIEW_VALUES, SEARCH_QUERY } from '../../constants'
import TableFilters, { FilterIProps } from '../../features/filters/components/tableFilter'
import FiltersComponent, { FilterIProps as OldFilterIProps } from '../../features/filters/filters'
import ButtonsCardListView from '../ButtonsCardListView/buttonsCardListView'
import LoaderView from '../Loader/loader'
import TabbedFilter, { TabbedFilterProps } from '../TabbedFilter'
import NoDataFound from '../NoDataFound'
import { useIntl } from 'react-intl'
import { X as SvgX } from 'phosphor-react'
import React, { ReactElement, ReactNode, useCallback, useMemo } from 'react'
import { Tabs, TabItem, SearchField, TableHeaderProps } from '@lightbeamai/design-system'
import { useHistory } from 'react-router-dom'
import classNames from 'classnames'

export interface ListItemData {
  [key: string]: any
}

export interface TableData {
  list: ListItemData[]
  columns: ITableColumn[]
}

export type CustomTabItem = TabItem & {
  link: string
  isActive?: boolean
}

export type CustomPaginationProps = {
  showPaginationByDefault?: boolean
  hidePageNumbers?: boolean
  hasNextPage?: boolean
  hasPreviousPage?: boolean
  footerPaginationText?: string
  totalItemsForPagination?: number
}

export type LegendValuesLoadingContent = {
  title?: string
  subTitle?: string
}

interface IProps {
  pageNames?: CustomTabItem[]
  pageNamesStyle?: 'button' | 'line'
  pageName: string
  activeTab?: string
  cardData?: ListItemData[]
  tableData: TableData
  pageView?: PAGE_VIEW_VALUES
  totalCount: number
  pageSize?: number
  showLoader?: boolean
  showListLoader?: boolean
  showCardLoader?: boolean
  searchText?: string
  legend?: string | React.ReactElement
  cardsLegend?: ReactNode
  cardLegendClassName?: string
  showCardsLegend?: boolean
  showTableBorder?: boolean
  cardContainerClass?: string
  cardsHeader?: ReactElement
  tableHeader?: ReactElement
  header?: TableHeaderProps
  customTableLegend?: ReactElement
  actionButtons?: ReactElement | ReactElement[]
  cardActionButtons?: ReactElement | ReactElement[]
  filterActionButtons?: ReactElement | string
  emptyPageContent?: ReactElement | ReactElement[]
  isPaginatedSearch?: boolean
  showTableLegend?: boolean
  showLengendForSearch?: boolean
  tabbedFilterProps?: TabbedFilterProps
  isPaginated?: boolean
  alwaysShowSearch?: boolean
  className?: string
  renderCard?: (data: { [key: string]: unknown }) => ReactElement
  onChangeSearchText?: (value: string) => void
  handleChangePageView?: (pageView: PAGE_VIEW_VALUES) => void
  filterProps?: FilterIProps
  tableParams?: TableParams
  isCardsPaginated?: boolean
  legendTotalCount?: number // Todo: Temp fix for Partner page UX issue, helps to show total count on table legend in case of one extra entry(External Emails)
  miscellaneousRecordsCount?: number // Todo: Temp fix for Partner page UX issue, corrects Table Legend 'start' variable in case of an extra entry.
  showControls?: boolean
  controlsClassName?: string
  containerViewClassName?: string
  useOldFilter?: boolean
  tableLegendClassName?: string
  allowCustomise?: boolean
  customPaginationProps?: CustomPaginationProps
  customFilters?: React.ReactNode
  legendValuesLoading?: boolean
  legendValuesLoadingContent?: LegendValuesLoadingContent
  defaultPagination?: boolean
}

const TableCardViewV2 = ({
  cardData: cardDataList = [],
  tableData: { list: tableDataList, columns },
  pageView = PAGE_VIEW_VALUES.list,
  totalCount,
  pageSize = LIMIT_DEFAULT,
  legendTotalCount,
  miscellaneousRecordsCount,
  isPaginated = true,
  alwaysShowSearch = false,
  showLoader = false,
  showListLoader = false,
  showCardLoader = false,
  searchText = '',
  isPaginatedSearch = false,
  showTableLegend = true,
  showLengendForSearch = false,
  showTableBorder = true,
  legend,
  cardsLegend = '',
  cardLegendClassName = '',
  renderCard,
  handleChangePageView,
  filterProps,
  pageName,
  activeTab = '',
  cardContainerClass,
  cardsHeader = <></>,
  tableHeader,
  header,
  customTableLegend,
  actionButtons,
  cardActionButtons,
  filterActionButtons = '',
  emptyPageContent,
  tabbedFilterProps,
  className = '',
  onChangeSearchText,
  tableParams,
  isCardsPaginated,
  pageNames,
  pageNamesStyle = 'button',
  showControls = true,
  controlsClassName = '',
  containerViewClassName = '',
  useOldFilter = false,
  tableLegendClassName = '',
  allowCustomise = false,
  customPaginationProps,
  customFilters,
  legendValuesLoading,
  legendValuesLoadingContent,
  defaultPagination = false
}: IProps): ReactElement => {
  const intl = useIntl()

  const history = useHistory()
  const context = useUrlContext({ pageName })
  const searchQuery = context.queryParams[SEARCH_QUERY] as string
  // TODO: find a way to reset page filter in URL to 1 when search applied
  const page = searchQuery ? 1 : context.pageFilters[activeTab || IS_ALL][PAGE] || 1
  const totalPages = Math.ceil(totalCount / pageSize)
  const tableList = useMemo(
    () => tableDataList?.filter(({ expandedMasterId }) => !expandedMasterId) || [],
    [tableDataList]
  )
  const showPagination = totalPages > 1 && (isPaginatedSearch || !searchText)
  const showCardPagination = isCardsPaginated && totalPages > 1
  const isListView = pageView === PAGE_VIEW_VALUES.list

  // search
  const showSearch = onChangeSearchText && (alwaysShowSearch || isListView)
  const handleSearch = useCallback(
    (e) => onChangeSearchText && onChangeSearchText(e.target.value),
    []
  )
  const handleResetSearch = () => onChangeSearchText && onChangeSearchText('')

  // tabs management
  const handleTabChange = (key: string) => {
    const selectedTab = pageNames?.find((item) => item.key == key)
    history.push(selectedTab?.link)
  }
  const currentTab =
    pageNames?.find((item) => item.isActive)?.key ||
    pageNames?.find((item) => item.link.split('?')[0] === context.pathname)?.key ||
    '0'

  const getCustomHeader = useMemo(() => {
    const customHeader: ReactElement[] = []
    if (tableHeader) {
      customHeader.push(tableHeader)
    }
    if (customTableLegend) {
      customHeader.push(customTableLegend)
    } else if ((showLengendForSearch || !searchText || isPaginatedSearch) && showTableLegend) {
      customHeader.push(
        <TableLegend
          total={legendTotalCount || totalCount} //Todo - needs to be removed after There is a UX
          showing={tableList.length}
          pageSize={pageSize}
          page={page}
          miscellaneousRecordsCount={miscellaneousRecordsCount} //Todo - needs to be removed after There is a UX
          name={legend || intl.formatMessage({ id: 'table.legend.objects' })}
          className={`no-outer-spaces xs-m-0 ${tableLegendClassName}`}
          legendValuesLoading={legendValuesLoading}
          legendValuesLoadingContent={legendValuesLoadingContent}
          hideTotalFormatted={defaultPagination}
          // Pagination props
          showPaginatedHeader={true}
          pageName={pageName}
          totalPages={totalPages}
          totalCount={totalCount}
          activeTab={activeTab || IS_ALL}
          allowCustomise={allowCustomise}
          customPaginationProps={customPaginationProps}
        />
      )
    }

    if (actionButtons) {
      customHeader.push(<div className="action-buttons">{actionButtons}</div>)
    }

    if (customHeader.length > 0) {
      return <div className="action-row xs-p-0">{customHeader}</div>
    }

    return undefined
  }, [
    actionButtons,
    intl,
    isPaginatedSearch,
    legend,
    legendTotalCount,
    miscellaneousRecordsCount,
    page,
    pageSize,
    searchText,
    showTableLegend,
    tableHeader,
    tableList.length,
    totalCount,
    legendValuesLoading,
    legendValuesLoadingContent
  ])

  // customSearch field is not needed but leads to circular dependency failure
  const filterPropsHash = JSON.stringify({ ...filterProps, customSearch: undefined })
  const getFilters = useMemo(() => {
    if (useOldFilter) {
      if (filterProps) {
        return <FiltersComponent {...(filterProps as OldFilterIProps)} />
      }
    }

    return (
      <TableFilters
        filters={[]}
        context={context}
        pageName={pageName}
        // showSearch={!!showSearch}
        {...filterProps}
        pageView={pageView}
        showViewToggle={!!handleChangePageView}
        onViewToggle={(view) =>
          handleChangePageView && handleChangePageView(view as PAGE_VIEW_VALUES)
        }
        customFilters={customFilters}
      />
    )
    // !!pass ONLY primitive types inside dependencies to avoid unexpected side-effects
  }, [filterPropsHash, !!customFilters, useOldFilter, pageName, pageView, !!handleChangePageView])

  return (
    <div
      className={`table-card-view-v2 custom-scrollbar ${className}`}
      data-test-id="table-card-view"
    >
      {showLoader ? (
        <LoaderView showLoader={showLoader} className="table-cardview-loader" />
      ) : (
        <>
          {!!tabbedFilterProps && (
            <TabbedFilter {...tabbedFilterProps} className={'tabbed-filter-container'} />
          )}
          {pageNames && pageNamesStyle === 'line' && (
            <Tabs
              items={pageNames}
              variant="line"
              onChange={handleTabChange}
              className="tabs-line"
              value={currentTab}
            />
          )}

          {pageNames && pageNamesStyle === 'button' && (
            <Tabs
              items={pageNames}
              variant="button"
              value={currentTab}
              onChange={handleTabChange}
              className="xs-mb-16"
            />
          )}

          {showControls && (
            <div className={`controls ${showSearch ? 'with-search' : ''} ${controlsClassName}`}>
              {showSearch && (
                <div className="search-wrapper">
                  <div className="search-container">
                    <SearchField
                      size={32}
                      value={searchText}
                      onChange={handleSearch}
                      placeholder={intl.formatMessage({ id: 'list.search.placeholder' })}
                    />
                    {searchText && (
                      <button className="btn icon-box reset" onClick={handleResetSearch}>
                        <SvgX size={16} color="var(--color-black)" fill="bold" />
                      </button>
                    )}
                  </div>
                </div>
              )}
              {handleChangePageView && useOldFilter && (
                <ButtonsCardListView pageView={pageView} onClick={handleChangePageView} />
              )}
              {filterActionButtons}
              <>
                {/* {handleChangePageView || filterActionButtons ? <div className="divider" /> : <></>} */}

                {getFilters}
              </>
            </div>
          )}
          {isListView ? (
            <>
              {tableDataList?.length > 0 || showListLoader ? (
                <div className={`container content-wrapper xs-mb-16 ${containerViewClassName}`}>
                  <div
                    className={classNames('table-container', { 'no-border': !showTableBorder })}
                    id="table-card-view-v2-table"
                  >
                    <Table
                      columns={columns}
                      data={tableDataList}
                      tableParams={tableParams}
                      customHeader={getCustomHeader}
                      customPagination={
                        (customPaginationProps?.showPaginationByDefault ||
                          legendValuesLoading ||
                          (showPagination && isPaginated)) && (
                          <PaginationView
                            pageName={pageName}
                            currentPage={page}
                            totalPages={totalPages}
                            pageSize={pageSize}
                            total={totalCount}
                            activeTab={activeTab || IS_ALL}
                            className="xs-mb-16"
                            allowCustomise={allowCustomise}
                            customPaginationProps={customPaginationProps}
                            legendValuesLoading={legendValuesLoading}
                            totalItemsForPagination={customPaginationProps?.totalItemsForPagination}
                          />
                        )
                      }
                      header={header}
                      totalCount={totalCount}
                      showLoader={showListLoader || showLoader}
                    />
                  </div>
                </div>
              ) : (
                emptyPageContent || <NoDataFound className="xs-pt-76" />
              )}
            </>
          ) : (
            renderCard && (
              // Todo - segregate into a different component
              <>
                <LoaderView showLoader={showCardLoader} />
                {cardDataList.length > 0 && cardsHeader}
                {cardsLegend && (
                  <TableLegend
                    total={cardDataList.length}
                    showing={cardDataList.length}
                    page={page}
                    name={cardsLegend || intl.formatMessage({ id: 'table.legend.objects' })}
                    className={classNames(`no-outer-spaces`, {
                      [cardLegendClassName]: !!cardLegendClassName
                    })}
                  />
                )}
                {cardActionButtons && (
                  <div className={classNames(`action-buttons xs-mb-16`)}>{cardActionButtons}</div>
                )}
                <div
                  className={`${
                    (showCardPagination || customPaginationProps?.showPaginationByDefault) &&
                    'container content-wrapper xs-mb-16 xs-p-0'
                  } table-cards-listing ${containerViewClassName}`}
                >
                  {cardDataList.length > 0 || showCardLoader ? (
                    <>
                      <div
                        className={`${
                          cardContainerClass ? cardContainerClass : 'cards-list-container'
                        } xs-mb-16`}
                      >
                        {cardDataList.map((card, i) => renderCard({ card, key: `card-info-${i}` }))}
                      </div>
                      {/*  Todo - segregate into a different component */}
                      {(customPaginationProps?.showPaginationByDefault ||
                        (showCardPagination && showPagination && isPaginated)) && (
                        <PaginationView
                          pageName={pageName}
                          currentPage={page}
                          totalPages={totalPages}
                          activeTab={activeTab || IS_ALL}
                          total={totalCount}
                          className="xs-mb-16"
                          allowCustomise={allowCustomise}
                          customPaginationProps={customPaginationProps}
                          legendValuesLoading={legendValuesLoading}
                        />
                      )}
                    </>
                  ) : (
                    emptyPageContent || <NoDataFound className="xs-pt-76" />
                  )}
                </div>
              </>
            )
          )}
        </>
      )}
    </div>
  )
}

export default React.memo(TableCardViewV2)
